import { Suspense, useRef } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useParams } from 'react-router-dom';
import { lazy } from '@/lib/lazy-route';
import { RootRouteErrorFallback } from '@/router/RootRouteErrorFallback';
import { AppLayout } from '@/components/layout/AppLayout';
import { SupportChatBubble } from '@/components/support/SupportChatBubble';
import { SimpleAiJobBanner, SimpleAiJobTracker } from '@/components/eduflow/simple/SimpleAiJobBanner';
import { SpaRouteAnnouncer } from '@/components/accessibility/SpaRouteAnnouncer';
import { GlobalSimplifiedText } from '@/components/accessibility/GlobalSimplifiedText';
import ReadingMask from '@/components/accessibility/ReadingMask';
import SpeakOnSelection from '@/components/accessibility/SpeakOnSelection';
import GlobalImageHoverAudio from '@/components/accessibility/GlobalImageHoverAudio';
import { CookieConsentBanner } from '@/components/consent/CookieConsentBanner';
import { MetaPixelTracker } from '@/components/analytics/MetaPixelTracker';
import { SeoRouteHead } from '@/components/seo/SeoRouteHead';
import { InstitutionAppThemeLayout } from '@/components/layout/InstitutionAppTheme';
import { PublicLayout } from '@/components/public/PublicLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LEARNER_WORKSPACE_ROLES } from '@/lib/auth-role';
import { InstitutionFeatureGate } from '@/components/institution/InstitutionFeatureGate';
import { INSTITUTION_MENU_FLAGS } from '@/lib/institution-menu-flags';
import { isLyrraOne } from '@/lib/platform';
import { useAffiliateRefCapture } from '@/lib/lyrraone-affiliate';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { CustomDomainSiteGate } from '@/components/lyrraone/CustomDomainSiteGate';
import { LyrraOnePlanFeatureGate } from '@/components/lyrraone/LyrraOnePlanFeatureGate';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { useLanguageStore, type Language } from '@/stores/language.store';
import { useAuthStore } from '@/stores/auth.store';

const LANG_CODES = new Set<string>(['fr', 'en', 'lb', 'it', 'es', 'uk', 'ru', 'pt', 'de', 'ar', 'tr', 'zgh']);

function LearnersListRoute() {
  if (isLyrraOne()) {
    return <Navigate to="/marketing/contacts" replace />;
  }
  return (
    <SuspenseWrapper>
      <LearnersPage />
    </SuspenseWrapper>
  );
}

function EduFlowListRoute() {
  const location = useLocation();
  if (isLyrraOne()) {
    const params = new URLSearchParams(location.search);
    params.set('tab', 'flows');
    const qs = params.toString();
    return <Navigate to={`/dashboard?${qs}`} replace />;
  }
  return (
    <SuspenseWrapper>
      <EduFlowListPage />
    </SuspenseWrapper>
  );
}

function PodcastsListRoute() {
  const location = useLocation();
  if (isLyrraOne()) {
    const params = new URLSearchParams(location.search);
    params.set('tab', 'supports');
    const qs = params.toString();
    return <Navigate to={`/dashboard?${qs}`} replace />;
  }
  return (
    <SuspenseWrapper>
      <PodcastsPage />
    </SuspenseWrapper>
  );
}

function LanguageRedirect() {
  const { lang, '*': rest } = useParams<{ lang: string; '*': string }>();
  const setLanguage = useLanguageStore((s) => s.setLanguage);
  const isValid = lang != null && LANG_CODES.has(lang);
  const applied = useRef(false);

  if (isValid && !applied.current) {
    applied.current = true;
    setLanguage(lang as Language);
  }

  const target = isValid ? (rest ? `/${rest}` : '/') : '/';
  return <Navigate to={target} replace />;
}

/** Aligné sur `V2_RH_MODULE_ROLES` (API v2) — module RH dans l’app, hors CODIR (DRH seul). */
const RH_MODULE_APP_ROLES: string[] = ['drh', 'rh', 'resp_formation', 'institution', 'institution_admin'];

/** Anciens liens `/v2/campaigns/...` (module retiré — LyrraOne). */
function LegacyV2CampaignsRedirect() {
  return <Navigate to="/dashboard" replace />;
}

// Auth pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const GoogleAuthCallbackPage = lazy(() => import('@/pages/auth/GoogleAuthCallbackPage'));
const InstitutionRegisterPage = lazy(() => import('@/pages/auth/InstitutionRegisterPage'));
const LyrraOneTeamInvitePage = lazy(() => import('@/pages/auth/LyrraOneTeamInvitePage'));

// Dashboard
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const FormationDashboardPage = lazy(() => import('@/pages/dashboard/FormationDashboardPage'));
const PodcastDashboardPage = lazy(() => import('@/pages/dashboard/PodcastDashboardPage'));
const InstitutionToDashboardRedirect = lazy(() => import('@/pages/dashboard/InstitutionToDashboardRedirect'));
const FormateurInstitutionTabPanel = lazy(() =>
  import('@/components/dashboard/FormateurInstitutionTabPanel').then((m) => ({ default: m.FormateurInstitutionTabPanel })),
);
const ProducerAssignmentsPage = lazy(() => import('@/pages/dashboard/ProducerAssignmentsPage'));

// Podcasts
const PodcastsPage = lazy(() => import('@/pages/podcasts/PodcastsPage'));
const EpisodesPage = lazy(() => import('@/pages/podcasts/EpisodesPage'));
const EpisodeEditorPage = lazy(() => import('@/pages/podcasts/EpisodeEditorPage'));
const SimplePodcastPage = lazy(() => import('@/pages/podcasts/simple/SimplePodcastPage'));
const SimpleEpisodeEditorPage = lazy(() => import('@/pages/podcasts/simple/SimpleEpisodeEditorPage'));
const PodcastDocumentImportPage = lazy(() => import('@/pages/podcasts/PodcastDocumentImportPage'));
const PodcastPublicPage = lazy(() => import('@/pages/podcasts/PodcastPublicPage'));
const PodcastLearnerPlayPage = lazy(() => import('@/pages/podcasts/PodcastLearnerPlayPage'));

const TrainerWebinarsPage = lazy(() => import('@/pages/webinars/TrainerWebinarsPage'));
const WebinarRoomPage = lazy(() => import('@/pages/webinars/WebinarRoomPage'));

// EduFlow
const EduFlowDesignerPage = lazy(() => import('@/pages/eduflow/EduFlowDesignerPage'));
const SimpleStructurePage = lazy(() => import('@/pages/eduflow/simple/SimpleStructurePage'));
const SimpleChapterBuilderPage = lazy(() => import('@/pages/eduflow/simple/SimpleChapterBuilderPage'));
const EduFlowPlayerPage = lazy(() => import('@/pages/eduflow/EduFlowPlayerPage'));
const EduFlowParticipantPage = lazy(() => import('@/pages/eduflow/EduFlowParticipantPage'));
const EduFlowHubRedirect = lazy(() => import('@/pages/eduflow/EduFlowHubRedirect'));
const EduFlowListPage = lazy(() => import('@/pages/eduflow/EduFlowListPage'));
const EduFlowPresenterPage = lazy(() => import('@/pages/eduflow/EduFlowPresenterPage'));

// Store
const StoreIndexPage = lazy(() => import('@/pages/store/StoreIndexPage'));
const StoreFormationDetailPage = lazy(() => import('@/pages/store/StoreFormationDetailPage'));

// Funnels (LyrraOne)
const FunnelListPage = lazy(() => import('@/pages/funnel/FunnelListPage'));
const FunnelCreatePage = lazy(() => import('@/pages/funnel/FunnelCreatePage'));
const FunnelEditorPage = lazy(() => import('@/pages/funnel/FunnelEditorPage'));
const FunnelRootPage = lazy(() => import('@/pages/funnel/FunnelRootPage'));
const FunnelSalesPage = lazy(() => import('@/pages/funnel/FunnelSalesPage'));
const FunnelCheckoutPage = lazy(() => import('@/pages/funnel/FunnelCheckoutPage'));
const FunnelThankYouPage = lazy(() => import('@/pages/funnel/FunnelThankYouPage'));
const FunnelCapturePage = lazy(() => import('@/pages/funnel/FunnelCapturePage'));
const FunnelCustomStepPage = lazy(() => import('@/pages/funnel/FunnelCustomStepPage'));

// LyrraOne pages publiques
const LyrraOnePublicSitePage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicSitePage'));
const LyrraOneCreatorStorePage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOneCreatorStorePage'));
const LyrraOneCreatorStoreFormationPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOneCreatorStoreFormationPage'));
const LyrraOneCreatorStoreProductsPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOneCreatorStoreProductsPage'));
const LyrraOneCreatorStoreProductPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOneCreatorStoreProductPage'));
const LyrraOnePublicBundlePage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicBundlePage'));
const LyrraOnePublicSiteBlogPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicSiteBlogPage'));
const LyrraOnePublicAffiliatePage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicAffiliatePage'));
const LyrraOnePublicAffiliateDashboardPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicAffiliateDashboardPage'));
const LyrraOnePublicContactPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicContactPage'));
const LyrraOnePublicCommunitiesPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicCommunitiesPage'));
const LyrraOnePublicLegalPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicLegalPage'));
const LyrraOnePublicAcquisitionFormPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicAcquisitionFormPage'));
const LyrraOnePublicSiteBlogPostPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicSiteBlogPostPage'));
const LyrraOneCreatorPublicPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOneCreatorPublicPage'));
const LyrraOnePublicBookingPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicBookingPage'));
const LyrraOnePublicCommunityPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicCommunityPage'));
const LyrraOneCommunitySpacePage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOneCommunitySpacePage'));
const LyrraOnePublicWebinarPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOnePublicWebinarPage'));
const LyrraOneWebinarWatchPage = lazy(() => import('@/pages/lyrraone-marketing/public/LyrraOneWebinarWatchPage'));

// LyrraOne marketing hub (formateur)
const LyrraOneMarketingHubPage = lazy(() => import('@/pages/lyrraone-marketing/LyrraOneMarketingHubPage'));
const LoContactsPage = lazy(() => import('@/pages/lyrraone-marketing/LoContactsPage'));
const LoEmailPage = lazy(() => import('@/pages/lyrraone-marketing/LoEmailPage'));
const LoAutomationsPage = lazy(() => import('@/pages/lyrraone-marketing/LoAutomationsPage'));
const LoSellerPaymentPage = lazy(() => import('@/pages/lyrraone-marketing/LoSellerPaymentPage'));
const LoSitePage = lazy(() => import('@/pages/lyrraone-marketing/LoSitePage'));
const LoSiteBlogPage = lazy(() => import('@/pages/lyrraone-marketing/LoSiteBlogPage'));
const LoCreatorPagePage = lazy(() => import('@/pages/lyrraone-marketing/LoCreatorPagePage'));
const LoCrmPage = lazy(() => import('@/pages/lyrraone-marketing/LoCrmPage'));
const LoAcquisitionFormsPage = lazy(() => import('@/pages/lyrraone-marketing/LoAcquisitionFormsPage'));
const LoAcquisitionFormPreviewPage = lazy(() => import('@/pages/lyrraone-marketing/LoAcquisitionFormPreviewPage'));
const LoAffiliatePage = lazy(() => import('@/pages/lyrraone-marketing/LoAffiliatePage'));
const LoBookingPage = lazy(() => import('@/pages/lyrraone-marketing/LoBookingPage'));
const LoCommunitiesPage = lazy(() => import('@/pages/lyrraone-marketing/LoCommunitiesPage'));
const LoCommunityDetailPage = lazy(() => import('@/pages/lyrraone-marketing/LoCommunityDetailPage'));
const LoWebinarsPage = lazy(() => import('@/pages/lyrraone-marketing/LoWebinarsPage'));
const LoFormationBundlesPage = lazy(() => import('@/pages/lyrraone-marketing/LoFormationBundlesPage'));
const LoPhysicalProductsPage = lazy(() => import('@/pages/lyrraone-marketing/LoPhysicalProductsPage'));
const LoPhysicalOrdersPage = lazy(() => import('@/pages/lyrraone-marketing/LoPhysicalOrdersPage'));
const LoSmsPage = lazy(() => import('@/pages/lyrraone-marketing/LoSmsPage'));
const LoPromotionsPage = lazy(() => import('@/pages/lyrraone-marketing/LoPromotionsPage'));

// Admin
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'));

// Institution dashboard

// Marketing
const HomePage = lazy(() => import('@/pages/marketing/HomePage'));
const PricingPage = lazy(() => import('@/pages/marketing/MarketingPricingPage'));
const FeaturesPage = lazy(() => import('@/pages/marketing/MarketingFeaturesPage'));
const AboutPage = lazy(() => import('@/pages/marketing/AboutPageV2'));
const ContactPage = lazy(() => import('@/pages/marketing/MarketingContactPage'));
const AccessibilityPage = lazy(() => import('@/pages/marketing/MarketingAccessibilityPage'));
const BlogPage = lazy(() => import('@/pages/marketing/BlogPage'));
const BlogPostPage = lazy(() => import('@/pages/marketing/BlogPostPage'));
const UseCasesPage = lazy(() => import('@/pages/marketing/UseCasesPageV3'));
const PartnersPage = lazy(() => import('@/pages/marketing/PartnersPageV2'));
const PrivacyPage = lazy(() => import('@/pages/marketing/MarketingPrivacyPage'));
const TermsPage = lazy(() => import('@/pages/marketing/MarketingTermsPage'));
const CookiesPage = lazy(() => import('@/pages/marketing/CookiesPageV2'));
const PublicHelpPage = lazy(() => import('@/pages/marketing/PublicHelpPage'));
const PublicWebinarsPage = lazy(() => import('@/pages/marketing/PublicWebinarsPage'));
const DemoPage = lazy(() => import('@/pages/marketing/MarketingDemoPage'));
const DemoEntryPage = lazy(() => import('@/pages/lyrraone/DemoEntryPage'));

// V2 RH Module
const ManagerPage = lazy(() => import('@/pages/v2/ManagerPage'));
const MyTrainingPage = lazy(() => import('@/pages/v2/MyTrainingPage'));

// Profile & Error
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const NotFoundPage = lazy(() => import('@/pages/error/NotFoundPage'));

// Learners
const LearnerDashboardPage = lazy(() => import('@/pages/learners/LearnerDashboardPage'));
const LearnersPage = lazy(() => import('@/pages/learners/LearnersPage'));

// Learner dashboard (fullscreen)
const LearnerPage = lazy(() => import('@/pages/learner/LearnerPage'));

// Analytics
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage'));

// Media library
const VideosPage = lazy(() => import('@/pages/media/VideosPage'));
const VideoEditPage = lazy(() => import('@/pages/media/VideoEditPage'));
const VideoStudioPage = lazy(() => import('@/pages/media/VideoStudioPage'));
const SoundsPage = lazy(() => import('@/pages/media/SoundsPage'));
const ImagesPage = lazy(() => import('@/pages/media/ImagesPage'));
const DocumentsPage = lazy(() => import('@/pages/media/DocumentsPage'));
const MediaDepositsPage = lazy(() => import('@/pages/media/MediaDepositsPage'));
const PublicMediaDepositPage = lazy(() => import('@/pages/media/PublicMediaDepositPage'));

// Help
const HelpPage = lazy(() => import('@/pages/help/HelpPage'));

// Institution
const GroupsPage = lazy(() => import('@/pages/institution/GroupsPage'));
const WebhooksPage = lazy(() => import('@/pages/institution/WebhooksPage'));

// RH direct sidebar routes
const ReportsPage = lazy(() => import('@/pages/rh/ReportsPage'));
const IntegrationsPage = lazy(() => import('@/pages/rh/IntegrationsPage'));

// Manager
const TeamPage = lazy(() => import('@/pages/manager/TeamPage'));
const ReviewsPage = lazy(() => import('@/pages/manager/ReviewsPage'));

// Collaborator / Learner
const TrainingPage = lazy(() => import('@/pages/collaborator/TrainingPage'));
const SkillsPage = lazy(() => import('@/pages/collaborator/SkillsPage'));
const CertificatesPage = lazy(() => import('@/pages/collaborator/CertificatesPage'));

// Settings
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const LoEmailTemplateEditorPage = lazy(() => import('@/pages/settings/LoEmailTemplateEditorPage'));
const LoSmsTemplateEditorPage = lazy(() => import('@/pages/settings/LoSmsTemplateEditorPage'));

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSpinner fullScreen />}>{children}</Suspense>
);

/** Chat support global : présent sur toutes les vues sauf pages publiques / auth (logique dans le composant). */
function RootWithSupportChat() {
  useAffiliateRefCapture();
  const user = useAuthStore((s) => s.user);
  return (
    <>
      <SeoRouteHead />
      <SpaRouteAnnouncer />
      <GlobalSimplifiedText />
      <ReadingMask />
      <SpeakOnSelection />
      <GlobalImageHoverAudio />
      <Outlet />
      {user ? (
        <>
          <SimpleAiJobTracker />
          <SimpleAiJobBanner />
        </>
      ) : null}
      <SupportChatBubble />
      <CookieConsentBanner />
      <MetaPixelTracker />
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <RootWithSupportChat />,
    errorElement: <RootRouteErrorFallback />,
    children: [
  {
    element: <CustomDomainSiteGate />,
    children: [
  // Préfixes de locale explicites uniquement (évite que /marketing/* soit pris pour :lang).
  ...[...LANG_CODES].flatMap((code) => [
    { path: `${code}/*`, element: <LanguageRedirect /> },
    { path: code, element: <LanguageRedirect /> },
  ]),

  // Public routes with header/footer
  {
    element: <PublicLayout />,
    children: [
      {
        path: '/',
        element: <SuspenseWrapper><HomePage /></SuspenseWrapper>,
      },
      {
        path: '/pricing',
        element: <SuspenseWrapper><PricingPage /></SuspenseWrapper>,
      },
      {
        path: '/features',
        element: <SuspenseWrapper><FeaturesPage /></SuspenseWrapper>,
      },
      {
        path: '/about',
        element: <SuspenseWrapper><AboutPage /></SuspenseWrapper>,
      },
      {
        path: '/contact',
        element: <SuspenseWrapper><ContactPage /></SuspenseWrapper>,
      },
      {
        path: '/demo',
        element: <SuspenseWrapper><DemoPage /></SuspenseWrapper>,
      },
      {
        path: '/aide',
        element: <SuspenseWrapper><PublicHelpPage /></SuspenseWrapper>,
      },
      {
        path: '/accessibility',
        element: <SuspenseWrapper><AccessibilityPage /></SuspenseWrapper>,
      },
      {
        path: '/blog',
        element: <SuspenseWrapper><BlogPage /></SuspenseWrapper>,
      },
      {
        path: '/blog/:postSlug',
        element: <SuspenseWrapper><BlogPostPage /></SuspenseWrapper>,
      },
      {
        path: '/webinaires',
        element: <SuspenseWrapper><PublicWebinarsPage /></SuspenseWrapper>,
      },
      {
        path: '/store',
        element: <SuspenseWrapper><StoreIndexPage /></SuspenseWrapper>,
      },
      {
        path: '/store/:formationId',
        element: <SuspenseWrapper><StoreFormationDetailPage /></SuspenseWrapper>,
      },
      {
        path: '/use-cases',
        element: <SuspenseWrapper><UseCasesPage /></SuspenseWrapper>,
      },
      {
        path: '/partners',
        element: <SuspenseWrapper><PartnersPage /></SuspenseWrapper>,
      },
      {
        path: '/privacy',
        element: <SuspenseWrapper><PrivacyPage /></SuspenseWrapper>,
      },
      {
        path: '/terms',
        element: <SuspenseWrapper><TermsPage /></SuspenseWrapper>,
      },
      {
        path: '/cookies',
        element: <SuspenseWrapper><CookiesPage /></SuspenseWrapper>,
      },
      {
        path: '/login',
        element: <SuspenseWrapper><LoginPage /></SuspenseWrapper>,
      },
      {
        path: '/register',
        element: <SuspenseWrapper><RegisterPage /></SuspenseWrapper>,
      },
      {
        path: '/establishment/register',
        element: <SuspenseWrapper><InstitutionRegisterPage /></SuspenseWrapper>,
      },
      {
        path: '/forgot-password',
        element: <SuspenseWrapper><ForgotPasswordPage /></SuspenseWrapper>,
      },
      {
        path: '/reset-password',
        element: <SuspenseWrapper><ResetPasswordPage /></SuspenseWrapper>,
      },
      {
        path: '/invite/team/:token',
        element: <SuspenseWrapper><LyrraOneTeamInvitePage /></SuspenseWrapper>,
      },
      {
        path: '/auth/google/callback',
        element: <SuspenseWrapper><GoogleAuthCallbackPage /></SuspenseWrapper>,
      },
    ],
  },

  // Public EduFlow routes
  {
    path: '/podcasts/public/p/:podcastId',
    element: <SuspenseWrapper><PodcastPublicPage /></SuspenseWrapper>,
  },
  {
    path: '/podcasts/public',
    element: <SuspenseWrapper><PodcastPublicPage /></SuspenseWrapper>,
  },
  {
    path: '/podcasts/learner',
    element: <SuspenseWrapper><PodcastLearnerPlayPage /></SuspenseWrapper>,
  },
  {
    path: '/eduflow/flows/:flowId/public',
    element: <SuspenseWrapper><EduFlowPlayerPage /></SuspenseWrapper>,
  },
  {
    path: '/demo/:code',
    element: <SuspenseWrapper><DemoEntryPage /></SuspenseWrapper>,
  },
  {
    path: '/deposit/:token',
    element: <SuspenseWrapper><PublicMediaDepositPage /></SuspenseWrapper>,
  },
  {
    path: '/eduflow/participant/:globalToken',
    element: <SuspenseWrapper><EduFlowParticipantPage /></SuspenseWrapper>,
  },
  {
    path: '/f/:slug/capture',
    element: <SuspenseWrapper><FunnelCapturePage /></SuspenseWrapper>,
  },
  {
    path: '/f/:slug/checkout',
    element: <SuspenseWrapper><FunnelCheckoutPage /></SuspenseWrapper>,
  },
  {
    path: '/f/:slug/merci',
    element: <SuspenseWrapper><FunnelThankYouPage /></SuspenseWrapper>,
  },
  {
    path: '/f/:slug/vente',
    element: <SuspenseWrapper><FunnelSalesPage /></SuspenseWrapper>,
  },
  {
    path: '/f/:slug/:stepSlug',
    element: <SuspenseWrapper><FunnelCustomStepPage /></SuspenseWrapper>,
  },
  {
    path: '/f/:slug',
    element: <SuspenseWrapper><FunnelRootPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/boutique/:productId',
    element: <SuspenseWrapper><LyrraOneCreatorStoreProductPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/boutique',
    element: <SuspenseWrapper><LyrraOneCreatorStoreProductsPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/formations/packs/:bundleId',
    element: <SuspenseWrapper><LyrraOnePublicBundlePage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/formations/:formationId',
    element: <SuspenseWrapper><LyrraOneCreatorStoreFormationPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/formations',
    element: <SuspenseWrapper><LyrraOneCreatorStorePage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug',
    element: <SuspenseWrapper><LyrraOnePublicSitePage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/blog/:postSlug',
    element: <SuspenseWrapper><LyrraOnePublicSiteBlogPostPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/blog',
    element: <SuspenseWrapper><LyrraOnePublicSiteBlogPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/contact',
    element: <SuspenseWrapper><LyrraOnePublicContactPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/communautes',
    element: <SuspenseWrapper><LyrraOnePublicCommunitiesPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/cgv',
    element: <SuspenseWrapper><LyrraOnePublicLegalPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/terms',
    element: <SuspenseWrapper><LyrraOnePublicLegalPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/confidentialite',
    element: <SuspenseWrapper><LyrraOnePublicLegalPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/privacy',
    element: <SuspenseWrapper><LyrraOnePublicLegalPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/cookies',
    element: <SuspenseWrapper><LyrraOnePublicLegalPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/affiliate/dashboard',
    element: <SuspenseWrapper><LyrraOnePublicAffiliateDashboardPage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/affiliate',
    element: <SuspenseWrapper><LyrraOnePublicAffiliatePage /></SuspenseWrapper>,
  },
  {
    path: '/s/:slug/:pageSlug',
    element: <SuspenseWrapper><LyrraOnePublicSitePage /></SuspenseWrapper>,
  },
  {
    path: '/c/:slug',
    element: <SuspenseWrapper><LyrraOneCreatorPublicPage /></SuspenseWrapper>,
  },
  {
    path: '/book/:slug',
    element: <SuspenseWrapper><LyrraOnePublicBookingPage /></SuspenseWrapper>,
  },
  {
    path: '/book/:slug/:typeId',
    element: <SuspenseWrapper><LyrraOnePublicBookingPage /></SuspenseWrapper>,
  },
  {
    path: '/community/:slug',
    element: <SuspenseWrapper><LyrraOnePublicCommunityPage /></SuspenseWrapper>,
  },
  {
    path: '/community/:slug/space',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><LyrraOneCommunitySpacePage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/webinar/:slug',
    element: <SuspenseWrapper><LyrraOnePublicWebinarPage /></SuspenseWrapper>,
  },
  {
    path: '/webinar/:slug/watch',
    element: <SuspenseWrapper><LyrraOneWebinarWatchPage /></SuspenseWrapper>,
  },
  // Protected routes (require auth)

  // EduFlow designer — fullscreen (no sidebar)
  {
    path: '/eduflow/edit/:flowId',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><EduFlowDesignerPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/webinars/live/:joinCode',
    element: (
      <InstitutionAppThemeLayout>
        <SuspenseWrapper><WebinarRoomPage /></SuspenseWrapper>
      </InstitutionAppThemeLayout>
    ),
  },
  {
    path: '/webinars/register/:joinCode',
    element: (
      <InstitutionAppThemeLayout>
        <SuspenseWrapper><WebinarRoomPage /></SuspenseWrapper>
      </InstitutionAppThemeLayout>
    ),
  },
  {
    path: '/eduflow/flows/:flowId/play',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><EduFlowPlayerPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eduflow/flows/:flowId/present',
    element: (
      <ProtectedRoute requiredRoles={['teacher', 'admin', 'institution_admin', 'resp_formation']}>
        <SuspenseWrapper><EduFlowPresenterPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Podcast episode editor — fullscreen (no sidebar)
  {
    path: '/podcasts/episodes/new',
    element: (
      <ProtectedRoute>
        <Navigate to="/podcasts/episodes?new=1" replace />
      </ProtectedRoute>
    ),
  },
  {
    path: '/podcasts/episodes/:id',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><EpisodeEditorPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/podcasts/:podcastId/import-document',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><PodcastDocumentImportPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Video studio / editor — fullscreen (no sidebar). `studio` avant `:id` pour éviter la collision de route.
  {
    path: '/videos/studio',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><VideoStudioPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/videos/:id',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><VideoEditPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Aperçu formulaire d'acquisition — fullscreen (brouillon inclus)
  {
    path: '/marketing/acquisition-forms/:formId/preview',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><LoAcquisitionFormPreviewPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Admin panel — fullscreen (independent layout, no AppLayout sidebar)
  {
    path: '/admin',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <SuspenseWrapper><AdminPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Learner dashboard — fullscreen (independent layout)
  {
    path: '/learner',
    element: (
      <ProtectedRoute requiredRoles={[...LEARNER_WORKSPACE_ROLES]}>
        <SuspenseWrapper><LearnerPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  // Institution dashboard — redirige vers dashboard formateur unifié
  {
    path: '/institution',
    element: (
      <ProtectedRoute
        requiredRoles={[
          'institution',
          'institution_admin',
          'drh',
          'rh',
          'teacher',
          'resp_formation',
        ]}
      >
        <SuspenseWrapper><InstitutionToDashboardRedirect /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/institution/learners/:participantId',
    element: (
      <ProtectedRoute
        requiredRoles={[
          'institution',
          'institution_admin',
          'drh',
          'rh',
          'teacher',
          'resp_formation',
        ]}
      >
        <SuspenseWrapper><InstitutionToDashboardRedirect /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/institution/learners/:participantId/bilan',
    element: (
      <ProtectedRoute
        requiredRoles={[
          'institution',
          'institution_admin',
          'drh',
          'rh',
          'teacher',
          'resp_formation',
        ]}
      >
        <SuspenseWrapper><InstitutionToDashboardRedirect /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: <SuspenseWrapper><DashboardPage /></SuspenseWrapper>,
      },
      {
        path: 'dashboard/learners/:participantId',
        element: (
          <SuspenseWrapper>
            <FormateurInstitutionTabPanel activeTab="learners" />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'dashboard/learners/:participantId/bilan',
        element: (
          <SuspenseWrapper>
            <FormateurInstitutionTabPanel activeTab="learners" />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'dashboard/formation/:flowId',
        element: (
          <ProtectedRoute requiredRoles={['teacher', 'editor', 'admin', 'institution_admin', 'resp_formation']}>
            <SuspenseWrapper><FormationDashboardPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'dashboard/podcast/:podcastId',
        element: (
          <ProtectedRoute requiredRoles={['teacher', 'editor', 'admin', 'institution_admin', 'resp_formation']}>
            <SuspenseWrapper><PodcastDashboardPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'producer-assignments',
        element: (
          <ProtectedRoute requiredRoles={['teacher', 'resp_formation', 'institution_admin']}>
            <SuspenseWrapper><ProducerAssignmentsPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile',
        element: <SuspenseWrapper><ProfilePage /></SuspenseWrapper>,
      },

      // Liste des formations (formateur / rôles création)
      {
        path: 'eduflow/list',
        element: <EduFlowListRoute />,
      },
      {
        path: 'eduflow/simple/:flowId/structure',
        element: <SuspenseWrapper><SimpleStructurePage /></SuspenseWrapper>,
      },
      {
        path: 'eduflow/simple/:flowId/chapter/:chapterId',
        element: <SuspenseWrapper><SimpleChapterBuilderPage /></SuspenseWrapper>,
      },
      {
        path: 'funnels',
        element: <SuspenseWrapper><FunnelListPage /></SuspenseWrapper>,
      },
      {
        path: 'funnels/new',
        element: <SuspenseWrapper><FunnelCreatePage /></SuspenseWrapper>,
      },
      {
        path: 'funnels/:id/edit',
        element: <SuspenseWrapper><FunnelEditorPage /></SuspenseWrapper>,
      },

      // LyrraOne — marketing & vente (formateur)
      { path: 'marketing', element: <SuspenseWrapper><LyrraOneMarketingHubPage /></SuspenseWrapper> },
      { path: 'marketing/contacts', element: <SuspenseWrapper><LoContactsPage /></SuspenseWrapper> },
      { path: 'marketing/acquisition-forms', element: <SuspenseWrapper><LoAcquisitionFormsPage /></SuspenseWrapper> },
      { path: 'marketing/email', element: <SuspenseWrapper><LoEmailPage /></SuspenseWrapper> },
      { path: 'marketing/automations', element: <SuspenseWrapper><LoAutomationsPage /></SuspenseWrapper> },
      { path: 'marketing/site', element: <SuspenseWrapper><LoSitePage /></SuspenseWrapper> },
      { path: 'marketing/payments', element: <SuspenseWrapper><LoSellerPaymentPage /></SuspenseWrapper> },
      { path: 'marketing/site/blog', element: <SuspenseWrapper><LyrraOnePlanFeatureGate feature="blog"><LoSiteBlogPage /></LyrraOnePlanFeatureGate></SuspenseWrapper> },
      { path: 'marketing/creator-page', element: <SuspenseWrapper><LoCreatorPagePage /></SuspenseWrapper> },
      { path: 'marketing/crm', element: <SuspenseWrapper><LoCrmPage /></SuspenseWrapper> },
      { path: 'marketing/affiliate', element: <SuspenseWrapper><LyrraOnePlanFeatureGate feature="affiliateProgram"><LoAffiliatePage /></LyrraOnePlanFeatureGate></SuspenseWrapper> },
      { path: 'marketing/promotions', element: <SuspenseWrapper><LyrraOnePlanFeatureGate feature="coupons"><LoPromotionsPage /></LyrraOnePlanFeatureGate></SuspenseWrapper> },
      { path: 'marketing/booking', element: <SuspenseWrapper><LyrraOnePlanFeatureGate feature="booking"><LoBookingPage /></LyrraOnePlanFeatureGate></SuspenseWrapper> },
      { path: 'marketing/communities', element: <SuspenseWrapper><LyrraOnePlanFeatureGate feature="communities"><LoCommunitiesPage /></LyrraOnePlanFeatureGate></SuspenseWrapper> },
      { path: 'marketing/communities/:id', element: <SuspenseWrapper><LyrraOnePlanFeatureGate feature="communities"><LoCommunityDetailPage /></LyrraOnePlanFeatureGate></SuspenseWrapper> },
      { path: 'marketing/webinars', element: <SuspenseWrapper><LyrraOnePlanFeatureGate feature="webinars"><LoWebinarsPage /></LyrraOnePlanFeatureGate></SuspenseWrapper> },
      { path: 'marketing/formation-bundles', element: <SuspenseWrapper><LoFormationBundlesPage /></SuspenseWrapper> },
      { path: 'marketing/physical-products', element: <SuspenseWrapper><LyrraOnePlanFeatureGate feature="physicalProducts"><LoPhysicalProductsPage /></LyrraOnePlanFeatureGate></SuspenseWrapper> },
      { path: 'marketing/physical-orders', element: <SuspenseWrapper><LyrraOnePlanFeatureGate feature="physicalProducts"><LoPhysicalOrdersPage /></LyrraOnePlanFeatureGate></SuspenseWrapper> },
      { path: 'marketing/sms', element: <SuspenseWrapper><LyrraOnePlanFeatureGate feature="smsMarketing"><LoSmsPage /></LyrraOnePlanFeatureGate></SuspenseWrapper> },

      // Ancienne URL /eduflow → redirection (établissement → onglet flows, sinon liste formations)
      {
        path: 'eduflow',
        element: <SuspenseWrapper><EduFlowHubRedirect /></SuspenseWrapper>,
      },

      // Podcasts (list pages only — editor is fullscreen outside AppLayout)
      {
        path: 'podcasts',
        element: <PodcastsListRoute />,
      },
      {
        path: 'podcasts/episodes',
        element: <SuspenseWrapper><EpisodesPage /></SuspenseWrapper>,
      },
      {
        path: 'podcasts/simple/:podcastId',
        element: <SuspenseWrapper><SimplePodcastPage /></SuspenseWrapper>,
      },
      {
        path: 'podcasts/simple/:podcastId/episodes/:episodeId',
        element: <SuspenseWrapper><SimpleEpisodeEditorPage /></SuspenseWrapper>,
      },

      {
        path: 'webinars',
        element: <Navigate to="/marketing/webinars" replace />,
      },
      {
        path: 'webinars/enterprise',
        element: <Navigate to="/marketing/webinars" replace />,
      },

      // Learners
      {
        path: 'learners/:participantId',
        element: (
          <ProtectedRoute
            requiredRoles={['teacher', 'resp_formation', 'collaborateur', 'manager', 'institution_admin', 'admin']}
          >
            <SuspenseWrapper><LearnerDashboardPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'learners',
        element: (
          <ProtectedRoute
            requiredRoles={['teacher', 'resp_formation', 'collaborateur', 'manager', 'institution_admin', 'admin']}
          >
            <LearnersListRoute />
          </ProtectedRoute>
        ),
      },

      // Analytics
      {
        path: 'analytics',
        element: <SuspenseWrapper><AnalyticsPage /></SuspenseWrapper>,
      },

      // Media library
      {
        path: 'videos',
        element: <SuspenseWrapper><VideosPage /></SuspenseWrapper>,
      },
      {
        path: 'sounds',
        element: <SuspenseWrapper><SoundsPage /></SuspenseWrapper>,
      },
      {
        path: 'images',
        element: <SuspenseWrapper><ImagesPage /></SuspenseWrapper>,
      },
      {
        path: 'documents',
        element: <SuspenseWrapper><DocumentsPage /></SuspenseWrapper>,
      },
      {
        path: 'media/deposits',
        element: <SuspenseWrapper><MediaDepositsPage /></SuspenseWrapper>,
      },

      // Help
      {
        path: 'help',
        element: <SuspenseWrapper><HelpPage /></SuspenseWrapper>,
      },

      // Institution
      {
        path: 'groups',
        element: <SuspenseWrapper><GroupsPage /></SuspenseWrapper>,
      },
      {
        path: 'webhooks',
        element: <SuspenseWrapper><WebhooksPage /></SuspenseWrapper>,
      },

      // Direct sidebar routes for DRH/RH roles
      {
        path: 'campaigns',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'competencies',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'compliance',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'reports',
        element: <SuspenseWrapper><ReportsPage /></SuspenseWrapper>,
      },
      {
        path: 'catalog',
        element: <Navigate to="/dashboard?tab=flows" replace />,
      },
      {
        path: 'integrations',
        element: (
          <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.integrations_page}>
            <SuspenseWrapper><IntegrationsPage /></SuspenseWrapper>
          </InstitutionFeatureGate>
        ),
      },
      {
        path: 'requests',
        element: <Navigate to="/dashboard" replace />,
      },

      // Manager direct routes
      {
        path: 'team',
        element: <SuspenseWrapper><TeamPage /></SuspenseWrapper>,
      },
      {
        path: 'reviews',
        element: <SuspenseWrapper><ReviewsPage /></SuspenseWrapper>,
      },

      // Collaborator / Learner direct routes
      {
        path: 'training',
        element: <SuspenseWrapper><TrainingPage /></SuspenseWrapper>,
      },
      {
        path: 'skills',
        element: <SuspenseWrapper><SkillsPage /></SuspenseWrapper>,
      },
      {
        path: 'certificates',
        element: <SuspenseWrapper><CertificatesPage /></SuspenseWrapper>,
      },

      // Settings
      {
        path: 'settings',
        element: <SuspenseWrapper><SettingsPage /></SuspenseWrapper>,
      },
      {
        path: 'settings/email-templates/:id/edit',
        element: <SuspenseWrapper><LoEmailTemplateEditorPage /></SuspenseWrapper>,
      },
      {
        path: 'settings/sms-templates/:id/edit',
        element: <SuspenseWrapper><LoSmsTemplateEditorPage /></SuspenseWrapper>,
      },

      // V2 RH Module (ancien /rh/dashboard → espace établissement)
      {
        path: 'rh/dashboard',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'rh/codir',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'v2/campaigns/*',
        element: (
          <ProtectedRoute requiredRoles={RH_MODULE_APP_ROLES}>
            <LegacyV2CampaignsRedirect />
          </ProtectedRoute>
        ),
      },
      {
        path: 'rh/campaigns/*',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'rh/competencies/*',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'rh/compliance/*',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'rh/requests',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'rh/requests/*',
        element: <Navigate to="/dashboard" replace />,
      },
      /** Ancien module budget (retiré) : liens bookmarkés → dashboard. */
      {
        path: 'rh/budgets',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'rh/catalog',
        element: <Navigate to="/dashboard?tab=flows" replace />,
      },
      {
        path: 'rh/catalog/*',
        element: <Navigate to="/dashboard?tab=flows" replace />,
      },

      // Manager routes
      {
        path: 'manager/*',
        element: (
          <ProtectedRoute requiredRoles={['manager']}>
            <SuspenseWrapper><ManagerPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },

      // Collaborator routes
      {
        path: 'my-training/*',
        element: (
          <ProtectedRoute requiredRoles={['collaborateur', 'manager']}>
            <SuspenseWrapper><MyTrainingPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
    ],
  },

  // 404
  {
    path: '/:acquisitionCode',
    element: <SuspenseWrapper><LyrraOnePublicAcquisitionFormPage /></SuspenseWrapper>,
  },
  {
    path: '*',
    element: <SuspenseWrapper><NotFoundPage /></SuspenseWrapper>,
  },
    ],
  },
    ],
  },
]);

export function AppRouter() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}

export default AppRouter;
